import {
  Group,
  Stack,
  TextInput,
  Select,
  NumberInput,
  SelectItem,
  Text,
} from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import WarningText from 'app/views/components/warning-text/warning-text'
import { DealParams } from 'app/models/deal-params'
import {
  Option,
  currencyOptions,
  dayBasisOptions,
  roundingOptions,
  rateTypeOptions,
  strBooleanOptions,
} from 'app/models/dropdown-options'
import {
  ContractPeriod,
  FacilityParams,
  IroValues,
} from 'app/models/facility-params'
import CustomDatePicker from 'app/views/components/date-picker/date-picker-logic'
import { forwardRef, useEffect, useState, useImperativeHandle } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getWireInstructions } from 'app/state/ducks/wire-instructions/selectors'
import {
  formatDateToUTC,
  roundTo,
  stringToDate,
  toNumberInputValue,
} from 'app/utils/util-functions'
import { DraftLoanParams, LoanParams } from 'app/models/loan-params'
import { loadLoanAmounts, saveLoan } from 'app/state/ducks/loans/thunks'
import { amendLoan } from './loan-daml-actions'
import cleanUUID from 'app/views/components/functions/cleanUUID'
import { getEntities } from 'app/state/ducks/entities/selectors'
import { getDeals } from 'app/state/ducks/deals/selectors'
import FormWrapper from 'app/views/components/Form/FormWrapper'
import axios from 'axios'
import {
  ErrorNotification,
  SuccessNotification,
} from 'app/views/components/notifications/notification'
import { getIndexRateOptions } from 'app/state/ducks/index-rate-options/selectors'
import { getInterestRatess } from 'app/state/ducks/interest-rates/selectors'
import { loadInterestByIndex } from 'app/state/ducks/interest-rates/thunks'
import { getLoanAmounts, getLoansByFacility } from 'app/state/ducks/loans/selectors'
import { loanAmountsEntryAsOf } from './utilities/loan-amounts-as-of'
import { getLetterOfCredits } from 'app/state/ducks/letters-of-credits/selectors'
import { getBusinessDate } from 'app/state/ducks/business-date/selectors'
import { resetInterest } from './utilities/reset-interest';
import { calculateTermInterests } from './utilities/calculate-term-interest'
import { calculateNonTermInterests } from './utilities/calculate-non-term-interest'
import { changeContractPeriod } from './utilities/change-contract-period'
import { getPikConfig } from './utilities/pik-config-utils'
import {
  isMaturityDateEditable,
  resolveNonTermMaturityDate,
} from './utilities/maturity-date-rules'
import { deriveAllInRate, formatRatePercent, rateDiffers } from 'app/utils/rate-rounding'

export interface Props {
  form: UseFormReturnType<any, (values: any) => any>
  facility?: FacilityParams
  loan?: LoanParams
  setSaveButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>
  setFacility?: React.Dispatch<React.SetStateAction<any>>
  setLoan: React.Dispatch<React.SetStateAction<any>>
  isAmendment?: boolean
  isEdit?: boolean
  rateValidationError?: string | null
  pikValidationError?: string | null
  onRateFieldInteraction?: () => void  // Called when user interacts with rate or maturity fields
}

export interface ChildLoanRef {
  handleClick: () => void
}

const UpdatedLoanModal: React.ForwardRefRenderFunction<ChildLoanRef, Props> = ({
  form,
  loan,
  facility,
  setSaveButtonDisabled,
  setLoan,
  isAmendment,
  isEdit,
  rateValidationError,
  pikValidationError,
  onRateFieldInteraction,
}, ref) => {
  const dispatch = useDispatch()
  const entities = useSelector(getEntities)
  const deals = useSelector(getDeals)
  const wireInstructionList = useSelector(getWireInstructions)
  const allIndexes = useSelector(getIndexRateOptions)
  const dailyRates = useSelector(getInterestRatess)
  const { data: loans } = useSelector(getLoansByFacility)
  const lettersOfCredits = useSelector(getLetterOfCredits)
  const businessDate = useSelector(getBusinessDate)
  const loanAmounts = useSelector(getLoanAmounts)
  const loansAmount = loans.filter(loan => loan.status == 'Approved').map(loan => loan.amount).reduce((a, b) => Number(a) + Number(b), 0)
  const locsAmount = lettersOfCredits.filter(loc => loc.status == 'Approved').map(loc => loc.amount).reduce((a, b) => Number(a) + Number(b), 0)
  const totalLoanLocsAmount = Number(loansAmount) + Number(locsAmount)
  const [deal, setDeal] = useState<DealParams>()
  const [iro, setIro] = useState<IroValues>()
  const [contractPeriod, setContractPeriod] = useState<ContractPeriod>()
  const [entityOptions, setEntityOptions] = useState<Option[]>([])
  const [availableCurrecncies, setAvailableCurrecncies] = useState<Option[]>([])
  const [facilityContractPeriodOptions, setFacilityContractPeriodOptions] =

    useState<SelectItem[]>([])
  const [wireInstructionsOption, setWireInstructionOptions] = useState<
    Option[]
  >([])
  const [, setIsLoading] = useState(false)
  const [facilityindexOptions, setFacilityIndexOptions] = useState<Option[]>([])
  const [disableEndDate, setDisableEndDate] = useState(false)

  const pikConfig = getPikConfig(facility?.pikOption)

  useEffect(() => {
    if (!allIndexes || allIndexes.length === 0 || !facility) {
      return
    }

    // A facility carries its index options per currency (iroValues is keyed by
    // index AND currency), so only offer the ones quoted in the currency the
    // loan is being booked in. Whatever is already selected stays in the list
    // regardless — an existing loan must never render a blank index because its
    // option no longer matches the filter.
    const selectedCurrency = form.values.currency
    const currencyIros = facility.iroValues.filter(
      iro =>
        !selectedCurrency ||
        iro.currency === selectedCurrency ||
        iro.indexOption.id === form.values.indexOption
    )
    console.log('currencyIros', currencyIros)
    console.log('allIndexes', allIndexes)
    console.log('facility.iroValues', facility.iroValues)

    const facilityIndexes = [...new Set(currencyIros.map(iro => { return allIndexes.find(option => option.id === iro.indexOption.id) }))]

    setFacilityIndexOptions(
      facilityIndexes.map(indexOption => ({
        value: indexOption?.id ?? '',
        label: indexOption?.indexOption ?? '',
      })) ?? []
    )
    const selectedIndex = allIndexes.find(
      index => index.id === form.values.indexOption
    )
    if (selectedIndex) {
      dispatch(loadInterestByIndex(selectedIndex))
    }
  }, [allIndexes, facility, form.values.currency])

  // set entity options
  useEffect(() => {
    if (!facility || !entities) {
      return
    }
    setEntityOptions(
      facility.borrowers
        ? facility.borrowers.map(borrower => {
          const entity = entities.find(entity => entity.id === borrower.id)
          return {
            label: entity?.entityName ?? '',
            value: borrower.id,
          }
        })
        : []
    )

    if (!facility.isSublimit) {
      return
    }

    const swingline = facility.subLimits.find(
      sublimit => sublimit.subLimitsType === 'SwingLine'
    )

    if (!swingline) {
      return
    }
  }, [entities, loan, facility])

  // set Deal
  useEffect(() => {
    if (!deals || !facility) {
      return
    }
    const foundDeal = deals.find(deal => deal.id === facility.dealId.id)
    if (foundDeal) {
      setDeal(foundDeal)
    }
  }, [deals, facility])

  // set Available Currencies
  useEffect(() => {
    if (!facility) {
      return
    }
    setAvailableCurrecncies(
      currencyOptions.filter(option =>
        facility.currencies.includes(option.value)
      )
    )
  }, [facility])

  // set WireInstrutionOption on edit
  useEffect(() => {
    if (!loan) {
      return
    }
    setWireInstructionOptions(
      wireInstructionList
        .filter(
          wireInstruction =>
            cleanUUID(wireInstruction.entity.id) === form.values.borrower
        )
        .map(wireInstruction => ({
          label: `${wireInstruction.currency}_${wireInstruction.beneficiaryAccountNumber}`,
          value: wireInstruction.id ?? '',
        }))
    )
  }, [wireInstructionList, loan])

  // set contract period on edit
  useEffect(() => {
    if (!loan || !facility) {
      return
    }
    const indexRateOptions = facility.iroValues.find(
      iro =>
        iro.indexOption.id === form.values.indexOption &&
        form.values.currency === iro.currency
    )

    setIro(indexRateOptions)
    if (!indexRateOptions) {
      return
    }

    const contractPeriods = indexRateOptions.contractPeriod.map(
      cp => cp.contractPeriod
    )
    const filteredContractPeriodOptions = rateTypeOptions.filter(option =>
      contractPeriods.includes(option.value)
    )
    setFacilityContractPeriodOptions(filteredContractPeriodOptions)

    const selectedContractPeriod = indexRateOptions.contractPeriod.find(
      cp => cp.contractPeriod === form.values.contractPeriod
    )
    setContractPeriod(selectedContractPeriod)
  }, [facility, loan])

  const handleClick = async () => {
    await onSubmit()
  }

  useImperativeHandle(ref, () => ({
    handleClick,
  }))

  // set interest on edit
  useEffect(() => {
    if (!dailyRates || loan) {
      return
    }
    if (allIndexes.find(index => index.id === form.values.indexOption)?.indexType !== 'TermIndex') {
      calculateNonTermInterests(form, loan, dailyRates, allIndexes, iro, form.values?.startDate);
    }
  }, [dailyRates])

  useEffect(() => {
    setSaveButtonDisabled(!form.isValid())
  }, [form.isValid()])

  useEffect(() => {
    if (!allIndexes || allIndexes.length === 0 || !loan) {
      return
    }

    const selectedIndex = allIndexes.find(
      index => index.id === loan.indexOption.id
    )
    setDisableEndDate(
      !isMaturityDateEditable({
        indexType: selectedIndex?.indexType,
        isAmendment,
        isEdit,
      })
    )
  }, [allIndexes, loan, isAmendment, isEdit])

  // Load loan amounts history for amendments AND edits — both re-baseline the
  // form to as-of values (amendments: the amendment date; edits: the loan start
  // date, which is the effective date an edit applies from).
  useEffect(() => {
    if ((isAmendment || isEdit) && loan?.id) {
      dispatch(loadLoanAmounts(loan.id))
    }
  }, [isAmendment, isEdit, loan?.id])

  const changeIndexOption = (event: string | null) => {
    if (!event) {
      return
    }
    const selectedIndex = allIndexes.find(index => index.id === event)

    setDisableEndDate(
      !isMaturityDateEditable({
        indexType: selectedIndex?.indexType,
        isAmendment,
        isEdit,
      })
    )

    if (selectedIndex) {
      dispatch(loadInterestByIndex(selectedIndex))
    }
    form.setFieldValue('indexOption', event)
    form.setFieldValue('casValue', 0)
    form.setFieldValue('allInRate', '')
    form.setFieldValue('contractPeriod', '')
    setContractPeriod(undefined)
    const filteredIros =
      facility?.iroValues.find(
        iro => iro.indexOption.id === event && form.values.currency === iro.currency
      ) ?? undefined
    if (!filteredIros) {
      form.setFieldValue('rounding', '')
      form.setFieldValue('margin', 0)
      form.setFieldValue('dayBasis', '')
      resetInterest(form, loan)
      return
    }
    form.setFieldValue('rounding', filteredIros.rounding)
    form.setFieldValue('margin', Number(filteredIros.margin))
    form.setFieldValue('dayBasis', filteredIros.dayBasis)
    setIro(filteredIros)

    const contractPeriods = filteredIros.contractPeriod.map(
      cp => cp.contractPeriod
    )
    const filteredContractPeriodOptions = rateTypeOptions.filter(option =>
      contractPeriods.includes(option.value)
    )
    if (selectedIndex?.indexType !== 'TermIndex') {
      form.setFieldValue('contractPeriod', null)
      form.setFieldValue(
        'endDate',
        resolveNonTermMaturityDate({
          isEditable: isMaturityDateEditable({
            indexType: selectedIndex?.indexType,
            isAmendment,
            isEdit,
          }),
          currentEndDate: form.values.endDate,
          facilityMaturity: facility?.maturity
            ? stringToDate(facility.maturity)
            : null,
        })
      )
    } else {
      resetInterest(form, loan)
      setFacilityContractPeriodOptions(filteredContractPeriodOptions)
    }
  }

  const handleBorrowerChange = (event: string | null) => {
    if (!event) {
      return
    }
    form.setFieldValue('borrower', event)
    setWireInstructionOptions(
      wireInstructionList
        .filter(
          wireInstruction => cleanUUID(wireInstruction.entity.id) === event
        )
        .map(wireInstruction => ({
          label: `${wireInstruction.currency}_${wireInstruction.beneficiaryAccountNumber}`,
          value: wireInstruction.id ?? '',
        }))
    )
  }

  const createDraftLoan = async () => {
    try {
      const loanParams: DraftLoanParams = form.getTransformedValues()

      let response: any
      if (isAmendment || isEdit) {
        response = await amendLoan(loanParams, loan, dispatch)
      } else {
        response = await dispatch(saveLoan(loanParams, loan))
      }

      if (response.success) {
        if (!isAmendment && !isEdit) {
          SuccessNotification({
            title: `Loan ${loan ? 'Updated' : 'Created'} Successfully`,
            message: `Loan ${loan ? 'Updated' : 'Created'} Successfully`,
          })
        }
        setLoan(response.payload.data || response.payload)
      } else {
        if (!isAmendment && !isEdit) {
          ErrorNotification({
            title: `Failed to ${loan ? 'Update' : 'Create'} Loan`,
            message: response.payload ?? 'Please check inputs or try again later',
          })
        }
      }
      return response
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status == 400) {
        const data: any = e.response.data
        ErrorNotification({
          title: `Failed to ${isAmendment ? 'Create Amendment' : loan ? 'Update' : 'Create'} Loan`,
          message: data?.error ?? 'Please check inputs',
        })
      } else {
        ErrorNotification({
          title: `Failed to ${isAmendment ? 'Create Amendment' : loan ? 'Update' : 'Create'} Loan`,
          message: 'Please check inputs or try again later',
        })
      }
      return null
    }
  }

  const onSubmit = async () => {
    setIsLoading(true)
    await createDraftLoan()
    setIsLoading(false)
  }


  function onInterestBaseRateChange(value: number | ''): void {
    form.setFieldValue('interestBaseRate', value)
    if (iro) {
      const interestWithRounding = Number(
        roundTo(Number(value), iro.rounding)
      )
      form.setFieldValue('interestBaseRateWithRounding', Number(interestWithRounding))
    }
  }

  // All-In Rate with PIK Premium for display: ROUNDED Base Rate + Margin + CAS + PIK Premium.
  // This is what users see in the "All In Rate" field. Deriving the rounded rate here
  // too keeps it and the all-in rate from ever disagreeing (the ledger derives both on save).
  // The derived fields are dependencies too (guarded by `rateDiffers`) so a loan
  // loaded for edit/amendment after this effect gets corrected.
  const derivedRates = deriveAllInRate({
    interestBaseRate: form.values.interestBaseRate,
    rounding: form.values.rounding || iro?.rounding,
    margin: form.values.margin,
    casValue: form.values.casValue,
    pikPremium: form.values.pikPremium,
  })
  useEffect(() => {
    const { roundedBaseRate, allInRate } = derivedRates
    if (rateDiffers(form.values.interestBaseRateWithRounding, roundedBaseRate)) {
      form.setFieldValue('interestBaseRateWithRounding', roundedBaseRate)
    }
    if (rateDiffers(form.values.allInRate, allInRate)) {
      form.setFieldValue('allInRate', allInRate)
    }
  }, [derivedRates.roundedBaseRate, derivedRates.allInRate, form.values.interestBaseRateWithRounding, form.values.allInRate])

  // An FX rate only exists for a loan booked in a currency other than the
  // facility's base currency — the ledger requires one then, and rejects the
  // loan without it (LoanActions: ApproveLoan / ApproveLoanWithReprocessing).
  const isForeignCurrencyLoan =
    !!facility?.baseCurrency &&
    !!form.values.currency &&
    form.values.currency !== facility.baseCurrency

  useEffect(() => {
    // Switching back to the base currency drops the rate, so a stale value is
    // never submitted for a loan that no longer needs converting.
    if (!isForeignCurrencyLoan && form.values.fxRate !== null) {
      form.setFieldValue('fxRate', null)
    }
  }, [isForeignCurrencyLoan])

  // Non-destructive counterpart of changeIndexOption for the as-of baseline:
  // re-resolves the IRO, contract-period options, and daily-rate data for the
  // index in force on the as-of date WITHOUT resetting the historical field
  // values (changeIndexOption is a user-driven reset — it clears casValue,
  // allInRate, and overwrites margin/dayBasis from the IRO, which would
  // clobber the as-of values the caller just applied). Returns the IRO in
  // force for the as-of index (the current one when it already matches) so the
  // caller can apply IRO-dependent derivations (e.g. base-rate rounding).
  const syncIndexDerivedState = (asOfIndexId: string | undefined) => {
    if (!asOfIndexId || asOfIndexId === iro?.indexOption?.id) {
      return iro
    }
    const asOfIndex = allIndexes.find(index => index.id === asOfIndexId)
    if (asOfIndex) {
      dispatch(loadInterestByIndex(asOfIndex))
    }
    const asOfIro = facility?.iroValues.find(
      iroValue =>
        iroValue.indexOption.id === asOfIndexId &&
        form.values.currency === iroValue.currency
    )
    if (asOfIro) {
      setIro(asOfIro)
      const contractPeriods = asOfIro.contractPeriod.map(cp => cp.contractPeriod)
      setFacilityContractPeriodOptions(
        rateTypeOptions.filter(option => contractPeriods.includes(option.value))
      )
    }
    return asOfIro ?? iro
  }

  // Re-baseline form fields to as-of values when amendment date changes.
  // Intentionally overwrites any user-typed rate edits — the baseline changed.
  // A cleared picker (null) keeps the current baseline untouched.
  const handleAmountChanges = (amendmentDate: Date | null) => {
    if (!amendmentDate) {
      return
    }
    const dateStr = formatDateToUTC(amendmentDate)
    const entry = loanAmountsEntryAsOf(loanAmounts, dateStr)
    const source = entry ?? null
    const asOfIndexId = (source?.indexOption ?? loan?.indexOption)?.id

    // The as-of entry may have accrued under a different index than the loan's
    // current one (e.g. a later loan conversion) — keep index-derived state in
    // step, and round the as-of base rate with that index's rounding rule
    // (mirrors onInterestBaseRateChange, which the setValues path bypasses).
    const effectiveIro = syncIndexDerivedState(asOfIndexId)
    const baseRate = source != null ? Number(source.interestBaseRate ?? 0) : Number(loan?.interestBaseRate ?? 0)
    // The rounding mode recorded on the as-of LoanAmounts row wins, so the
    // derived-rates effect rounds the way that period was actually rated.
    const asOfRounding = source?.rounding ?? effectiveIro?.rounding ?? form.values.rounding

    form.setValues({
      amount: source ? Number(source.amount) : Number(loan?.amount ?? 0),
      indexOption: asOfIndexId ?? form.values.indexOption,
      dayBasis: source ? source.dayBasis : (loan?.dayBasis ?? ''),
      margin: source != null ? Number(source.margin ?? 0) : Number(loan?.margin ?? 0),
      interestBaseRate: baseRate,
      rounding: asOfRounding,
      ...(asOfRounding
        ? { interestBaseRateWithRounding: Number(roundTo(baseRate, asOfRounding)) }
        : {}),
      casValue: source != null ? Number(source.casValue ?? 0) : Number(loan?.casValue ?? 0),
    })
  }

  // Apply the as-of baseline on OPEN (and once the amounts history loads) — not
  // only when the user changes the date. The date is pre-filled by the stepper
  // (amendment → business date, edit → loan start date), so without this the
  // form shows current-loan values until the user touches the picker.
  useEffect(() => {
    if (!isAmendment && !isEdit) {
      return
    }
    if (!form.values.amendmentDate || !loanAmounts || loanAmounts.length === 0) {
      return
    }
    handleAmountChanges(form.values.amendmentDate)
  }, [loanAmounts])

  return (
    <FormWrapper title={`${isEdit ? 'Edit' : isAmendment ? 'Amend' : loan ? 'Edit' : 'Create'} Loan`}>
      <Stack className="loan-form">
        {isAmendment && (
          <Group noWrap w="100%">
            <CustomDatePicker
              label={'Amendment Date'}
              date={form.values.amendmentDate || (() => {
                // Only use business date as fallback if it's before the loan end date
                const loanEndDate = loan?.endDate ? stringToDate(loan.endDate) : null
                if (loanEndDate && businessDate > loanEndDate) {
                  return null // Force user to select a valid date
                }
                return businessDate
              })()}
              setDate={(value: any) => {
                handleAmountChanges(value)
                form.setFieldValue('amendmentDate', value)
              }}
              holidayCalendars={deal?.holidayCalendar ?? []}
              errorMsg={form.errors.amendmentDate as any}
              required
            />
          </Group>
        )}
        {isEdit && (
          <Group noWrap w="100%">
            <CustomDatePicker
              label={'Amendment Date'}
              date={form.values.amendmentDate || businessDate}
              // datepicker is disabled for edits so do nothing.  back end expects an undefined for loan edits (as opposed to amendments which require a date) and handles accordingly
              setDate={() => { }}
              holidayCalendars={deal?.holidayCalendar ?? []}
              disabled={true}
              required={false}
            />
          </Group>
        )}
        <Group noWrap>
          <TextInput
            readOnly
            className="applyGrayBackground"
            label="Deal"
            w="100%"
            value={deal?.dealName ?? deal?.id ?? ''}
          ></TextInput>
          <TextInput
            readOnly
            className="applyGrayBackground"
            label="Facility"
            w="100%"
            value={facility?.name ?? facility?.id ?? ''}
          ></TextInput>
        </Group>

        <Group noWrap>
          <CustomDatePicker
            label={'Start Date'}
            date={form.values.startDate}
            errorMsg={form.errors.startDate as any}
            required
            disabled={isAmendment || isEdit}
            setDate={(value: any) => {
              form.setFieldValue('startDate', value)
              if (allIndexes.find(index => index.id === form.values.indexOption)?.indexType !== 'TermIndex') {
                calculateNonTermInterests(form, loan, dailyRates, allIndexes, iro, value);
              } else {
                calculateTermInterests(form, loan, dailyRates, iro, contractPeriod, value);
              }
            }}
            holidayCalendars={deal?.holidayCalendar ?? []}
          />
          <CustomDatePicker
            disabled={disableEndDate}
            label={'Maturity Date'}
            date={form.values.endDate}
            setDate={(value: any) => {
              form.setFieldValue('endDate', value)
              // If this is an amendment or edit that changes maturity date, 
              // we let the backend handle interest recalculation
              // Trigger interaction callback for validation
              onRateFieldInteraction?.()
            }}
            holidayCalendars={deal?.holidayCalendar ?? []}
            errorMsg={form.errors.endDate as any}
          />
        </Group>
        <Group noWrap>
          <Select
            searchable
            clearable
            withAsterisk
            data={availableCurrecncies}
            label="Select currency"
            placeholder="Currency"
            w="100%"
            disabled={isAmendment || isEdit}
            {...form.getInputProps('currency')}
            onChange={value => {
              form.getInputProps('currency').onChange(value)
              // Any currency change invalidates a previously entered rate
              form.setFieldValue('fxRate', null)
            }}
          />

          <NumberInput
            withAsterisk
            w="100%"
            label="Amount"
            id="amount"
            precision={2}
            placeholder="Enter Amount"
            disabled={isAmendment || isEdit}
            parser={value =>
              value ? value.replace(/\$\s?|(,*)/g, '') : ''
            }
            formatter={value =>
              !Number.isNaN(parseFloat(value ?? ''))
                ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                : ''
            }
            {...form.getInputProps('amount')}
          />
          {isForeignCurrencyLoan && (
            <NumberInput

              w="100%"
              precision={6}
              label="FX Rate"
              placeholder={`${form.values.currency} → ${facility?.baseCurrency}`}
              // No percent/currency formatting: an FX rate is a plain
              // multiplier (1 EUR = 1.085 USD), not a rate or an amount.
              min={0}
              // Editable in amendment and edit too, unlike currency and amount:
              // the ledger allows re-rating a loan (fxRate is not in
              // validateAmendableFields, and the amendment paths re-convert
              // facility utilization from the new rate).
              {...form.getInputProps('fxRate')}
              value={toNumberInputValue(form.values.fxRate)}
            />
          )}
        </Group>
        {facility && form.values.amount && Number(form.values.amount) <= facility?.amount && Number(form.values.amount) + totalLoanLocsAmount > facility?.amount ?
          <Group noWrap>
            <Text w="100%"></Text>
            <Text w="100%" className='warningText'>Warning: The creation of this Loan will exceed the Facility Availability</Text>
          </Group>
          : null}

        <Group noWrap>
          <Select
            searchable
            data={entityOptions ?? []}
            label="Borrower"
            w="100%"
            placeholder="Select borrower"
            disabled={isAmendment || isEdit}
            onChange={event => {
              handleBorrowerChange(event)
            }}
            value={form.values.borrower}
          />
          <Select
            searchable
            data={wireInstructionsOption}
            label="Borrower Wire Instructions"
            placeholder="Select Wire Instructions"
            w="100%"
            disabled={isAmendment || isEdit}
            {...form.getInputProps('borrowerPaymentInstructions')}
          />
          <TextInput
            w="100%"
            label="Agent Loan ID"
            name="agentLoanId"
            id="agentLoanId"
            placeholder="Enter Agent Loan ID"
            {...form.getInputProps('agentLoanId')}
          />
        </Group>

        <Group noWrap>
          <Select
            clearable
            searchable
            w="100%"
            placeholder="Is it SwingLine"
            label="Swing Line (Y/N)"
            disabled={isAmendment || isEdit || !facility?.isSublimit || form?.values?.isNonProRata === 'true' ? true : false}
            data={strBooleanOptions}
            {...form.getInputProps('isSwingLine')}
          />
          <Select
            clearable
            searchable
            w="100%"
            placeholder="Is it NonProRata"
            label="Non-Prorata (Y/N)"
            disabled={isAmendment || isEdit || form?.values?.isSwingLine === 'true' ? true : false}
            data={strBooleanOptions}
            {...form.getInputProps('isNonProRata')}
          />
        </Group>

        <Group noWrap>
          <Select
            searchable
            label="Index Rate"
            placeholder="Select Index Rate"
            w="100%"
            disabled={isAmendment || isEdit}
            data={[...facilityindexOptions]}
            {...form.getInputProps('indexOption')}
            onChange={e => {
              changeIndexOption(e)
            }}
          />
          <Select
            searchable
            disabled={isAmendment || isEdit || form.values.indexOption.length === 0 || allIndexes.find(index => index.id === form.values.indexOption)?.indexType !== 'TermIndex'}
            label="Contract Period"
            placeholder="Select Contract Period"
            w="100%"
            data={facilityContractPeriodOptions}
            {...form.getInputProps('contractPeriod')}
            onChange={e => {
              changeContractPeriod(form, iro, loan, dailyRates, e, setContractPeriod, form.values?.startDate);
            }}
          />
        </Group>

        <Group noWrap>
          <Select
            searchable
            data={roundingOptions}
            readOnly
            disabled={isAmendment || isEdit}
            label="Interest Base Rate Rounding"
            w="100%"
            {...form.getInputProps('rounding')}
          />
          <Select
            searchable
            readOnly
            disabled={isAmendment || isEdit}
            data={dayBasisOptions}
            label="Day basis"
            w="100%"
            {...form.getInputProps('dayBasis')}
          />
        </Group>

        <Group noWrap>
          <NumberInput
            w="100%"
            precision={5}
            label="Interest Rate "
            parser={value =>
              value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
            }
            formatter={value =>
              !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
            }
            {...form.getInputProps('interestBaseRate')}
            onChange={onInterestBaseRateChange}
            onFocus={() => onRateFieldInteraction?.()}
          />
          <NumberInput
            readOnly
            className="applyGrayBackground"
            w="100%"
            precision={5}
            label="Interest Base Rate with Rounding"
            parser={value =>
              value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
            }
            formatter={value =>
              !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
            }
            {...form.getInputProps('interestBaseRateWithRounding')}
          />
        </Group>

        <Group noWrap>
          <NumberInput
            w="100%"
            precision={5}
            label="Margin"
            parser={value =>
              value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
            }
            formatter={value =>
              !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
            }
            {...form.getInputProps('margin')}
            onFocus={() => onRateFieldInteraction?.()}
          />
          {(form.values.indexOption && allIndexes.find(index => index.id === form.values.indexOption)?.indexType === 'TermIndex') && (
            <NumberInput
              // readOnly
              w="100%"
              precision={5}
              label="CAS Rate"
              placeholder='Select a Contract Period'
              parser={value =>
                value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
              }
              formatter={value => {
                const numValue = parseFloat(value ?? '')
                if (Number.isNaN(numValue)) return ''

                // For edit/amendment mode, show CAS if value exists, even without contract period
                // This handles merged loans which don't have contract period but do have CAS
                if ((isEdit || isAmendment) && numValue !== 0) {
                  return formatRatePercent(numValue)
                }

                // For new loan creation, require contract period to be selected first
                if (form.values.contractPeriod) {
                  return formatRatePercent(numValue)
                }

                return ''
              }}
              {...form.getInputProps('casValue')}
              onFocus={() => onRateFieldInteraction?.()}
            />
          )}
          <NumberInput
            readOnly
            className="applyGrayBackground"
            disabled={(() => {
              const allInRateWithoutPik = form?.values?.allInRate - (Number(form?.values?.pikPremium) || 0)
              return (iro?.iroCap !== null || iro?.iroFloor !== null) &&
                (Number(iro?.iroCap) < allInRateWithoutPik || Number(iro?.iroFloor) > allInRateWithoutPik)
            })()}
            w="100%"
            precision={5}
            label="All In Rate"
            parser={value =>
              value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
            }
            // Shown as stored: the all-in rate carries margin, CAS and PIK
            // premium, none of which are subject to the base rate's rounding.
            formatter={value => (Number.isNaN(Number(value)) ? '0.00000 %' : formatRatePercent(value))}
            {...form.getInputProps('allInRate')}
          />
        </Group>

        {/* Display rate validation error for edits and amendments */}
        {(isEdit || isAmendment) && rateValidationError && (
          <Group mt={5} noWrap>

            <Text w="100%" color="red" size="sm">
              {rateValidationError}
            </Text>
          </Group>
        )}

        <Group noWrap>
          <Select
            searchable
            readOnly
            disabled
            label="PIK Option"
            w="100%"
            data={[
              { label: 'PIK all', value: 'PIK_All' },
              { label: 'PIK optional', value: 'PIK_Optional' },
              { label: 'No PIK', value: 'No_PIK' },
              { label: 'PIK schedule', value: 'PIK_Scheduled' },
            ]}
            value={facility?.pikOption}
            styles={{
              input: {
                backgroundColor: '#f5f5f5',
                cursor: 'not-allowed',
              },
            }}
          />
          {/* PIK On/Off toggle for PIK Optional facilities */}
          {pikConfig.showNewLoanPikToggle && (
            <Select
              label="PIK (On/Off)"
              w="100%"
              placeholder="Select"
              data={[
                { label: 'On', value: 'On' },
                { label: 'Off', value: 'Off' },
              ]}
              {...form.getInputProps('pikToggle')}
              onChange={(value) => {
                form.setFieldValue('pikToggle', value)
                // If turning PIK off, clear PIK values
                if (value === 'Off') {
                  form.setFieldValue('pikPremium', 0)
                  form.setFieldValue('pikInterestPercent', 0)
                  form.setFieldValue('cashInterestPercent', 0)
                }
              }}
            />
          )}
        </Group>

        {(pikConfig.showNewLoanPikFields ||
          (pikConfig.showNewLoanPikFieldsWhenEnabled && form.values.pikToggle === 'On')) && (
            <>
              <Group noWrap>
                <NumberInput
                  w="100%"
                  precision={5}
                  label="PIK Premium (%)"
                  parser={value =>
                    value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                  }
                  formatter={value =>
                    !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
                  }
                  {...form.getInputProps('pikPremium')}
                />
              </Group>
              <Group noWrap>
                <NumberInput
                  w="100%"
                  precision={5}
                  label="PIK Interest (%)"
                  parser={value =>
                    value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                  }
                  formatter={value =>
                    !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
                  }
                  {...form.getInputProps('pikInterestPercent')}
                />
                <NumberInput
                  w="100%"
                  precision={5}
                  label="Cash Interest (%)"
                  parser={value =>
                    value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                  }
                  formatter={value =>
                    !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
                  }
                  {...form.getInputProps('cashInterestPercent')}
                />
              </Group>

              {pikValidationError && (
                <div style={{ marginTop: '10px' }}>
                  <WarningText type="error" text={pikValidationError} />
                </div>
              )}
            </>
          )}

        {(iro?.iroCap !== null || iro?.iroFloor !== null) && <Group noWrap>
          <NumberInput
            readOnly
            className="applyGrayBackground"
            disabled={Number(iro?.iroCap) > form?.values?.allInRate}
            w="100%"
            precision={5}
            label="Cap All-In-Rate"
            parser={value =>
              value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
            }
            formatter={value =>
              !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
            }
            value={Number(iro?.iroCap) ?? 0.00000}
          />
          <NumberInput
            readOnly
            className="display-only-field"
            disabled={Number(iro?.iroFloor) < form?.values?.allInRate}
            w="100%"
            precision={5}
            label="Floor All-In-Rate"
            parser={value =>
              value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
            }
            formatter={value =>
              !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
            }
            value={Number(iro?.iroFloor) ?? 0.00000}
          />
        </Group>}
      </Stack>
    </FormWrapper >
  )
}

export default forwardRef(UpdatedLoanModal)
